/**
 * Utilitário para integração com Google Analytics 4 (GA4)
 * 
 * Nota: O script do GA4 é carregado diretamente no index.html
 * Este arquivo apenas fornece utilitários para verificação e debug
 */

import { environment } from '../config/environment';
import { logger } from './logger.util';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

let ga4MeasurementId: string | null = null;

/**
 * Configura o Google Analytics 4
 * O script já está carregado no HTML, apenas configura
 */
export function initializeGA4(): void {
  const measurementId = environment.ga4MeasurementId;

  if (!measurementId) {
    logger.warnWithContext('GA4', 'Measurement ID não configurado. Analytics desabilitado.');
    return;
  }

  ga4MeasurementId = measurementId;

  // Garantir que dataLayer e gtag existem (já devem estar no HTML)
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  if (typeof window.gtag !== 'function') {
    logger.warnWithContext('GA4', 'gtag não encontrado. Verifique se o script está no HTML.');
    return;
  }

  // Reconfigurar o GA4 (já foi configurado no HTML, mas garantimos aqui também)
  window.gtag('config', measurementId, {
    send_page_view: false // Desabilitar page_view automático, vamos enviar manualmente
  });

  // Armazenar measurementId globalmente para uso posterior
  (window as any).__gaMeasurementId = measurementId;
}

/**
 * Verifica se o GA4 está disponível e pronto para uso
 */
export function isGA4Available(): boolean {
  // Verificar se dataLayer existe e é um array
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) {
    return false;
  }

  // Verificar se gtag é uma função
  if (typeof window.gtag !== 'function') {
    return false;
  }

  return true;
}

/**
 * Retorna o Measurement ID configurado
 */
export function getGA4MeasurementId(): string | null {
  return ga4MeasurementId;
}

/**
 * Define o user_id no GA4
 * @param userId ID do usuário (geralmente o ID retornado pela API de autenticação)
 */
export function setGA4UserId(userId: string | null): void {
  if (!isGA4Available() || !window.gtag) {
    logger.warnWithContext('GA4', 'Não é possível setar user_id: GA4 não disponível');
    return;
  }

  if (!ga4MeasurementId) {
    logger.warnWithContext('GA4', 'Não é possível setar user_id: Measurement ID não configurado');
    return;
  }

  if (!userId) {
    // Limpar user_id se null (logout)
    window.gtag('config', ga4MeasurementId, {
      user_id: null
    });
    return;
  }

  // Setar user_id no GA4
  window.gtag('config', ga4MeasurementId, {
    user_id: userId
  });
}

/**
 * Função de debug para verificar o status do GA4
 * Pode ser chamada no console do navegador: window.debugGA4()
 */
export function setupGA4Debug(): void {
  (window as any).debugGA4 = function() {
    console.log('=== GA4 Debug Info ===');
    console.log('Measurement ID:', ga4MeasurementId || environment.ga4MeasurementId);
    console.log('dataLayer existe:', !!window.dataLayer);
    console.log('dataLayer é array:', Array.isArray(window.dataLayer));
    console.log('dataLayer length:', window.dataLayer?.length || 0);
    console.log('dataLayer conteúdo:', window.dataLayer);
    console.log('gtag existe:', typeof window.gtag);
    console.log('gtag é função:', typeof window.gtag === 'function');
    
    const scriptElement = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
    console.log('Script carregado no HTML:', scriptElement !== null);
    if (scriptElement) {
      console.log('Script src:', (scriptElement as HTMLScriptElement).src);
    }
    
    // Verificar requisições de rede
    console.log('=== Verificando requisições de rede ===');
    console.log('Abra a aba Network e filtre por "google-analytics" ou "collect"');
    
    // Testar envio de evento
    if (isGA4Available() && window.gtag) {
      console.log('=== Testando envio de evento ===');
      window.gtag('event', 'test_event', {
        test_param: 'test_value'
      });
      console.log('Evento de teste enviado. Verifique no dataLayer e na aba Network.');
    } else {
      console.warn('GA4 não está disponível para teste');
    }
    
    console.log('=== Fim do Debug ===');
  };
}
